import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import clsx from "clsx";
import { Link } from "@remix-run/react";
import type {
  EventV2,
  MicroCMSAPIContentResponse,
} from "@/lib/microcms.server";

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

interface EventsListProps {
  events: MicroCMSAPIContentResponse<EventV2>[];
}

export function EventsList({ events }: EventsListProps) {
  // 1. 月の範囲を決定（eventsの最小・最大日付から）
  const allDates = events.flatMap((ev) =>
    ev.schedules.map((s) => dayjs(s.date))
  );
  if (allDates.length === 0) return null;
  const monthStart = allDates.reduce((a, b) => (a.isBefore(b) ? a : b));
  const monthEnd = allDates.reduce((a, b) => (a.isAfter(b) ? a : b));

  // 2. 本予約完了・仮予約中 の日付をhiddenDaysに
  const hiddenDays = new Set<string>();
  events.forEach(ev => {
    const isHidden = ev.reservation_status?.includes("本予約完了") || ev.reservation_status?.includes("仮予約中");
    if (isHidden) {
      ev.schedules.forEach(s => {
        const d = dayjs(s.date);
        if (d.isBetween(monthStart, monthEnd, "day", "[]")) {
          hiddenDays.add(d.format("YYYY-MM-DD"));
        }
      });
    }
  });

  // 3. 一般公開イベントの範囲を組み立て
  const publicRanges = events
    .filter((ev) => ev.reservation_status?.includes("一般公開"))
    .map((ev) => {
      const dates = ev.schedules
        .map((s) => dayjs(s.date))
        .filter((d) => d.isBetween(monthStart, monthEnd, "day", "[]"))
        .sort((a, b) => a.unix() - b.unix());
      if (!dates.length) return null;
      return {
        type: "public" as const,
        start: dates[0],
        end: dates[dates.length - 1],
        event: ev,
      };
    })
    .filter((x): x is Exclude<typeof x, null> => x !== null)
    .sort((a, b) => a.start.unix() - b.start.unix());

  // 4. 公開イベント範囲の隙間を「開放日」として埋める
  type DisplayItem =
    | {
        type: "public";
        start: dayjs.Dayjs;
        end: dayjs.Dayjs;
        event: MicroCMSAPIContentResponse<EventV2>;
      }
    | { type: "open"; start: dayjs.Dayjs; end: dayjs.Dayjs };

  const segments: DisplayItem[] = [];
  let cursor = monthStart;

  // openセグメントを日ごとに分割し、hiddenDaysの日はスキップ
  function pushOpenSegments(start: dayjs.Dayjs, end: dayjs.Dayjs) {
    let segStart = start;
    let d = start;
    while (d.isSameOrBefore(end, "day")) {
      if (hiddenDays.has(d.format("YYYY-MM-DD"))) {
        if (segStart.isBefore(d, "day")) {
          segments.push({ type: "open", start: segStart, end: d.subtract(1, "day") });
        }
        segStart = d.add(1, "day");
      }
      d = d.add(1, "day");
    }
    if (segStart.isSameOrBefore(end, "day")) {
      segments.push({ type: "open", start: segStart, end: end });
    }
  }

  publicRanges.forEach((range) => {
    const gapStart = cursor;
    const gapEnd = range.start.subtract(1, "day");
    if (gapStart.isSameOrBefore(gapEnd, "day")) {
      pushOpenSegments(gapStart, gapEnd);
    }
    segments.push(range);
    cursor = range.end.add(1, "day");
  });

  if (cursor.isSameOrBefore(monthEnd, "day")) {
    pushOpenSegments(cursor, monthEnd);
  }

  // 5.表示
  return (
    <ul className="block">
      {segments.map((item, i) => {
        const isRange = !item.start.isSame(item.end, "day");
        if (item.type === "public") {
          const v = item.event;
          return (
            <li
              key={`${v.id}-${item.start.format("YYYY-MM-DD")}`}
              className={clsx(
                "block mb-4",
                i !== 0 && ["pt-4", "border-t", "border-t-gray-100"]
              )}
            >
              <Link
                to={`/events/article/${v.id}`}
                aria-label={v.title}
                className={clsx("block hover:underline focus:underline")}
              >
                <div className={clsx("flex flex-col space-y-2")}>
                  <div className="text-xs leading-normal font-bold font-sans">
                    <div className="flex items-center space-x-2">
                      <span className="text-brand-royalBlue">
                        {isRange
                          ? `${item.start.format(
                              "YYYY/M/D (dd)"
                            )} ~ ${item.end.format("YYYY/M/D (dd)")}`
                          : item.start.format("YYYY/M/D (dd)")}
                      </span>
                      <span aria-hidden="true" className="text-gray-700">
                        ・
                      </span>
                      <span
                        className={clsx(
                          "block",
                          (v.venue[0] === "屋内" ||
                            v.venue[0] === "屋内半面" ||
                            v.venue[0] === "屋内全面") &&
                            "text-brand-green",
                          v.venue[0] === "屋外" && "text-brand-red",
                          v.venue[0] === "屋内外（両方）" && "text-brand-purple"
                        )}
                      >
                        {v.venue[0]}
                      </span>
                    </div>
                  </div>
                  <span className="font-bold text-gray-900">{v.title}</span>
                </div>
              </Link>
            </li>
          );
        } else {
          return (
            <li
              key={`open-${item.start.format("YYYY-MM-DD")}-${item.end.format(
                "YYYY-MM-DD"
              )}-${i}`}
              className={clsx(
                "block mb-4",
                i !== 0 && ["pt-4", "border-t", "border-t-gray-100"],
                "rounded"
              )}
            >
              <div>
                <span className="block text-xs text-gray-400 font-normal">
                  {item.start.isSame(item.end, "day")
                    ? `${item.start.format("YYYY/M/D (dd)")} 9:00 ~ 21:00`
                    : `${item.start.format("YYYY/M/D (dd)")} ~ ${item.end.format("YYYY/M/D (dd)")} 9:00 ~ 21:00`}
                </span>
                <h3 className="text-gray-400 font-normal">
                  一般開放日（どなたでもご利用可能です）
                </h3>
              </div>
            </li>
          );
        }
      })}
    </ul>
  );
}
